import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, CreateActivity, UpdateActivity } from '../types';
import { activitiesAPI } from '../services/api';
import { navigateWithRedirect } from '../utils/redirectUtils';
import DateInput from './DateInput';

const ActivityForm: React.FC = () => {
  const { referenceType, referenceId, activityId } = useParams<{
    referenceType: string;
    referenceId: string;
    activityId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState<CreateActivity | UpdateActivity>({
    title: '',
    createdDate: new Date().toISOString().split('T')[0],
    referenceType: referenceType as 'Project' | 'Customer',
    referenceId: referenceId ? Number(referenceId) : 0,
  });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (activityId) {
      activitiesAPI.getActivity(Number(activityId))
        .then(activity => {
          setFormData({
            id: activity.id,
            title: activity.title,
            createdDate: activity.createdDate.split('T')[0],
            referenceType: activity.referenceType,
            referenceId: activity.referenceId,
          } as UpdateActivity);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [activityId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.createdDate) newErrors.createdDate = 'Date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (activityId) {
        await activitiesAPI.updateActivity(Number(activityId), formData as UpdateActivity);
      } else {
        await activitiesAPI.createActivity(formData);
      }
      // Navigate back to redirect URL or go back to previous page
      const defaultPath = referenceType === 'Project'
        ? `/projects/${referenceId}`
        : `/customers/${referenceId}`;
      navigateWithRedirect(navigate, defaultPath, searchParams);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border" role="status" />
        <span className="visually-hidden">Loading...</span>
      </div>
    );
  }

  return (
    <div className="card activity-form-card">
      <div className="card-header">
        <h5>{activityId ? 'Edit Activity' : 'Create Activity'}</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit} className="activity-form">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Title <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
              {errors.title && <div className="invalid-feedback">{errors.title}</div>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Date <span className="text-danger">*</span></label>
              <DateInput
                className={`form-control ${errors.createdDate ? 'is-invalid' : ''}`}
                name="createdDate"
                value={formData.createdDate}
                onChange={(value) => handleInputChange({ target: { name: 'createdDate', value } } as any)}
                required
              />
              {errors.createdDate && <div className="invalid-feedback">{errors.createdDate}</div>}
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2 activity-form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => {
              const defaultPath = referenceType === 'Project'
                ? `/projects/${referenceId}`
                : `/customers/${referenceId}`;
              navigateWithRedirect(navigate, defaultPath, searchParams);
            }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {activityId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ActivityForm;
